// Palworld map data layer.
//
// Two datasets live here:
//  1. MAP_REGIONS — the Palpagos Islands regions placed on a 1000×700 canvas in
//     their correct relative geography (main island in the centre, Sea Breeze
//     Archipelago to the south, Ice Wind Island north-west, Mount Obsidian
//     south-west, Sakurajima and Feybreak as separate islands, the Sunreach
//     sky isles east). Each region maps to the spawn keys used by pals-data so
//     the interactive map can list the Pals that appear there.
//  2. MAP_VIEWS — the map library: a themed map for every layer players
//     actually search for (alphas, towers, dungeons, ore, fast travel, and the
//     expansion maps), each paired with its own guide + FAQ.
//
// Artwork note: the in-game Palpagos map is Pocketpair intellectual property,
// so every map on the site is drawn by MapArt as original vector terrain using
// the real island layout rather than a copied screenshot.

import { BOSSES } from "@/lib/bosses-data";

export interface MapRegion {
  slug: string;
  name: string;
  biome: string;
  levels: string;
  /** Centre + radius on the 1000×1000 game-map canvas. */
  cx: number;
  cy: number;
  r: number;
  /** Which pals-data spawn labels belong to this region. */
  spawnKeys: string[];
  summary: string;
  threats: string;
  resources: string[];
}

export const MAP_REGIONS: MapRegion[] = [
  {
    slug: "sea-breeze-archipelago",
    name: "Sea Breeze Archipelago",
    biome: "Coastal",
    levels: "Lv 1–10",
    cx: 478,
    cy: 572,
    r: 30,
    spawnKeys: ["Sea Breeze Archipelago"],
    summary:
      "The southern starter beaches. Calm spawns, dense berry bushes and the first fast-travel statues make this the safest place to place a first Palbox.",
    threats: "Alpha Chillet and roaming Rushoar packs on the inland ridges.",
    resources: ["Berries", "Wood", "Stone", "Palium (shallow)"],
  },
  {
    slug: "windswept-hills",
    name: "Windswept Hills",
    biome: "Grassland",
    levels: "Lv 1–15",
    cx: 519,
    cy: 499,
    r: 40,
    spawnKeys: ["Windswept Hills"],
    summary:
      "The default spawn plateau and home of the first Syndicate Tower. Flat build sites, three ore clusters and every early work-suitability Pal spawn here.",
    threats: "Zoe & Grizzbolt in the Rayne Syndicate Tower; Syndicate camps on the roads.",
    resources: ["Ore", "Wood", "Stone", "Red Berry Seeds"],
  },
  {
    slug: "marsh-island",
    name: "Marsh Island",
    biome: "Swamp",
    levels: "Lv 15–25",
    cx: 601,
    cy: 481,
    r: 30,
    spawnKeys: ["Marsh of Whispers"],
    summary:
      "Humid south-eastern wetlands. Water and Grass Pals dominate, and the shallow pools are the cheapest early source of high-quality Pal oil and paldium.",
    threats: "Alpha Elphidran and poison-spitting Cawgnito flocks at night.",
    resources: ["Paldium Fragments", "High Quality Pal Oil", "Wood"],
  },
  {
    slug: "bamboo-groves",
    name: "Bamboo Groves",
    biome: "Forest",
    levels: "Lv 20–30",
    cx: 699,
    cy: 401,
    r: 35,
    spawnKeys: ["Bamboo Groves"],
    summary:
      "Eastern bamboo forest and the Free Pal Alliance stronghold. The only reliable early bamboo supply, needed for every mid-game crossbow and refrigerator recipe.",
    threats: "Lily & Lyleen in the Free Pal Alliance Tower; Alpha Lunaris on the ridge.",
    resources: ["Bamboo", "Ore", "Wool"],
  },
  {
    slug: "verdant-brook",
    name: "Verdant Brook",
    biome: "Jungle",
    levels: "Lv 30–40",
    cx: 561,
    cy: 421,
    r: 45,
    spawnKeys: ["Verdant Brook", "Forgotten Mines"],
    summary:
      "Dense north-eastern jungle wrapped around the Forgotten Mines. Highest alpha density on the main island and the best coal-plus-ore combination base site.",
    threats: "Alpha Warsect, Alpha Elizabee and level 40 dungeon spawns.",
    resources: ["Coal", "Ore", "Bamboo", "Skill Fruit Trees"],
  },
  {
    slug: "eastern-wild-island",
    name: "Eastern Wild Island",
    biome: "Jungle",
    levels: "Lv 25–35",
    cx: 763,
    cy: 357,
    r: 33,
    spawnKeys: ["Oil Rig Stronghold"],
    summary:
      "The far-eastern island chain and offshore rig. Reachable only by flying mount or raft, which makes it the quietest late-game farm on the map.",
    threats: "Rig guards with firearms; no safe respawn point without a satellite base.",
    resources: ["Crude Oil", "Sulfur", "Chromite"],
  },
  {
    slug: "mount-obsidian",
    name: "Mount Obsidian",
    biome: "Volcanic",
    levels: "Lv 40–50",
    cx: 429,
    cy: 451,
    r: 64,
    spawnKeys: ["Sanctuary of Ashes", "Sulfur Highlands"],
    summary:
      "South-western volcanic belt. Every Fire-type worth condensing lives here, and it is the game's densest sulfur and coal region for ammunition production.",
    threats: "Heat damage without Heat Resistant armour; Alpha Blazamut and Jormuntide Ignis.",
    resources: ["Sulfur", "Coal", "Ore", "Ancient Civilization Parts"],
  },
  {
    slug: "twilight-dunes",
    name: "Twilight Dunes",
    biome: "Desert",
    levels: "Lv 30–40",
    cx: 754,
    cy: 204,
    r: 64,
    spawnKeys: ["Deep Sand Dunes"],
    summary:
      "Northern desert basin with day-and-night temperature swings. Ground and Dark Pals swap on the same spawn points after dusk.",
    threats: "Heat by day, cold by night; Alpha Anubis patrols the central plateau.",
    resources: ["Ore", "Sulfur", "Quartz", "Bones"],
  },
  {
    slug: "ice-wind-island",
    name: "Ice Wind Island",
    biome: "Snow",
    levels: "Lv 25–35",
    cx: 547,
    cy: 175,
    r: 60,
    spawnKeys: ["Tundra Ridge"],
    summary:
      "North-western snowfield. The only mid-game source of Ice Pals for Cooler Box work suitability and the fastest Chillet and Reindrix farm.",
    threats: "Constant cold damage without Cold Resistant armour; Alpha Penking on the coast.",
    resources: ["Ice", "Ore", "Wool", "Pal Fluids"],
  },
  {
    slug: "astral-mountains",
    name: "Astral Mountains",
    biome: "Mountain",
    levels: "Lv 45–50",
    cx: 655,
    cy: 299,
    r: 34,
    spawnKeys: ["Astral Mountain", "Twilight Cliffs"],
    summary:
      "The frozen northern spine of Palpagos and the approach to the final Syndicate Tower. Legendary spawns begin here.",
    threats: "Victor & Shadowbeak; Alpha Frostallion on the summit plateau.",
    resources: ["Ore", "Coal", "Ancient Civilization Parts", "Pure Quartz"],
  },
  {
    slug: "sakurajima",
    name: "Sakurajima",
    biome: "Sakura",
    levels: "Lv 45–55",
    cx: 424,
    cy: 249,
    r: 44,
    spawnKeys: ["Sakurajima Coast", "Moonless Shore"],
    summary:
      "The separate south-western expansion island. Sakura groves, the Bellanoir summoning site and the Pal Terminal that sells expansion-only gear.",
    threats: "Bellanoir raid spawns and level 50 Syndicate elites near the shrine.",
    resources: ["Cement", "Pal Metal Ingot mats", "Ancient Civilization Parts"],
  },
  {
    slug: "feybreak",
    name: "Feybreak",
    biome: "Ashland",
    levels: "Lv 50–60",
    cx: 281,
    cy: 688,
    r: 88,
    spawnKeys: ["Feybreak Isles", "Ancient Ritual Site"],
    summary:
      "The far-western end-game island: ash plains, the Oculus Gate and the densest alpha population in the game. Requires the Feybreak questline to reach safely.",
    threats: "Level 60 alphas, Xenolord raid access and permanent hostile patrols.",
    resources: ["Hexolite Quartz", "Chromite", "Ancient Civilization Parts"],
  },
  {
    slug: "sunreach-sky-islands",
    name: "Sunreach Sky Islands",
    biome: "Sky",
    levels: "Lv 50+",
    cx: 889,
    cy: 350,
    r: 30,
    spawnKeys: [],
    summary:
      "Floating isles added in Palworld 1.0, reachable only on a high-stamina flier. Rare skill-fruit trees and gliding-only chest routes.",
    threats: "Fall damage and stamina loss; no fast travel until you plant a statue.",
    resources: ["Skill Fruits", "Pure Quartz", "Chests"],
  },
  {
    slug: "world-tree",
    name: "World Tree",
    biome: "Raid",
    levels: "Lv 55+",
    cx: 620,
    cy: 531,
    r: 28,
    spawnKeys: ["World Tree"],
    summary:
      "A separate instanced map rather than part of Palpagos. Endgame raid arena and the source of the rarest Lucky variants on the site's roster.",
    threats: "Scaling raid waves; wipe returns you to the entrance, not a bed.",
    resources: ["Raid drops", "Slabs", "Legendary schematics"],
  },
];

export const MAP_REGION_BY_SLUG: Record<string, MapRegion> = Object.fromEntries(
  MAP_REGIONS.map((r) => [r.slug, r]),
);

export const BIOME_COLORS: Record<string, string> = {
  Coastal: "#38BDF8",
  Grassland: "#22C55E",
  Swamp: "#84CC16",
  Forest: "#14B8A6",
  Jungle: "#10B981",
  Volcanic: "#F97316",
  Desert: "#EAB308",
  Snow: "#7DD3FC",
  Mountain: "#A78BFA",
  Sakura: "#F472B6",
  Ashland: "#EF4444",
  Sky: "#C4B5FD",
  Raid: "#F59E0B",
};

/** A themed map in the map library. Each one has its own guide + FAQ. */
export interface MapView {
  slug: string;
  name: string;
  short: string;
  desc: string;
  /** Marker layer this map highlights. */
  layer: "regions" | "alpha" | "tower" | "dungeon" | "resource" | "travel" | "island" | "raid";
  palette: [string, string];
  /** Region slugs this map focuses on (empty = whole world). */
  focus: string[];
  bosses: string[];
  strategy: string;
  keywords: string;
}

export const MAP_VIEWS: MapView[] = [
  {
    slug: "palpagos-overview",
    name: "Palpagos Islands Overview Map",
    short: "Every region, biome and level band in one view.",
    desc: "The full Palpagos Islands map with all 14 regions placed in their correct positions, color-coded by biome and labelled with recommended level bands.",
    layer: "regions",
    palette: ["#38BDF8", "#22C55E"],
    focus: [],
    bosses: ["Zoe & Grizzbolt", "Lily & Lyleen", "Victor & Shadowbeak"],
    strategy:
      "Read the map clockwise from Sea Breeze Archipelago: south for levels 1–15, east for 20–40, north for 45–50, then the western islands for 50–60. Never skip a band by more than ten levels or the elemental damage penalty makes fights unwinnable.",
    keywords:
      "palpagos islands map, palworld world map regions, palworld interactive map, palworld region map, palworld level regions map, palworld world map 1.0",
  },
  {
    slug: "alpha-boss",
    name: "Alpha Pal Boss Map",
    short: "Field alpha arenas ranked by level and region.",
    desc: "Where every field Alpha Pal spawns across Palpagos, which regions have the highest alpha density, and the order to farm them for schematics.",
    layer: "alpha",
    palette: ["#F97316", "#EF4444"],
    focus: ["verdant-brook", "mount-obsidian", "feybreak", "astral-mountains"],
    bosses: ["Alpha Chillet", "Alpha Anubis", "Alpha Blazamut", "Alpha Frostallion"],
    strategy:
      "Alphas do not scale, so bring a Pal with the alpha's elemental weakness and out-range it. Field alphas respawn on an in-game day timer, which makes a satellite base with a bed next to a dense cluster the fastest schematic farm in the game.",
    keywords:
      "palworld alpha pal locations, palworld alpha boss map, palworld field boss locations, alpha pal coordinates palworld, palworld alpha farming route",
  },
  {
    slug: "tower-boss",
    name: "Syndicate Tower Map",
    short: "All five tower arenas and their approach routes.",
    desc: "Every Syndicate Tower on the Palpagos map with its faction, boss level, elemental weakness and the safest approach route for each arena.",
    layer: "tower",
    palette: ["#A78BFA", "#6366F1"],
    focus: ["windswept-hills", "bamboo-groves", "astral-mountains", "sakurajima", "feybreak"],
    bosses: ["Zoe & Grizzbolt", "Lily & Lyleen", "Victor & Shadowbeak"],
    strategy:
      "Towers are hard-timed gear checks. Fly in, plant a statue outside the door, and treat each tower as the level gate for the region beyond it — clearing in map order keeps every fight inside a five-level window.",
    keywords:
      "palworld tower locations, palworld syndicate tower map, palworld tower boss order, where are the towers in palworld, palworld tower boss levels",
  },
  {
    slug: "dungeon",
    name: "Dungeon & Sealed Realm Map",
    short: "Cave entrances, sealed realms and level bands.",
    desc: "Dungeon entrances and Sealed Realm shrines by region, with the level band inside each and which Pals are locked behind them.",
    layer: "dungeon",
    palette: ["#8B5CF6", "#4C1D95",],
    focus: ["windswept-hills", "verdant-brook", "mount-obsidian", "feybreak"],
    bosses: ["Dungeon boss rotation", "Sealed Realm guardians"],
    strategy:
      "Dungeon interiors are level-banded by their host region, not by the entrance you use, so a Feybreak cave is level 55 even from a low ledge. Clear with two ranged Pals, loot chests first, then fight — the boss room is optional and the chest loot is the real reward.",
    keywords:
      "palworld dungeon locations, palworld sealed realm locations, palworld cave map, palworld dungeon levels, sealed realm palworld guide",
  },
  {
    slug: "resource",
    name: "Ore, Coal & Sulfur Map",
    short: "Every farmable node cluster by region.",
    desc: "Resource map for ore, coal, sulfur, quartz, chromite and hexolite quartz, with the best combined-node base sites on each island.",
    layer: "resource",
    palette: ["#EAB308", "#B45309"],
    focus: ["windswept-hills", "verdant-brook", "mount-obsidian", "twilight-dunes", "feybreak"],
    bosses: ["Alpha Digtoise", "Alpha Blazamut"],
    strategy:
      "Build over node clusters instead of running routes: a Palbox placed so that six or more ore nodes sit inside the radius turns two Mining-suitability Pals into an infinite ingot supply. Coal and sulfur overlap only on Mount Obsidian, which is why it is the standard ammunition base.",
    keywords:
      "palworld ore locations, palworld coal locations, palworld sulfur map, palworld best mining base, palworld chromite location, hexolite quartz palworld",
  },
  {
    slug: "fast-travel",
    name: "Fast Travel & Base Site Map",
    short: "Statues of Power, teleports and the best base plots.",
    desc: "Fast-travel statue coverage across Palpagos plus the flat, defensible plots recommended for your three base slots at each stage of the game.",
    layer: "travel",
    palette: ["#22D3EE", "#0EA5E9"],
    focus: ["sea-breeze-archipelago", "windswept-hills", "verdant-brook", "mount-obsidian"],
    bosses: ["Syndicate raid waves"],
    strategy:
      "Activate every statue you pass — they double as Lifmunk Effigy turn-in points and respawn anchors. Keep base one in Windswept Hills for ore, base two in Verdant Brook for coal, and base three mobile for whichever region you are clearing.",
    keywords:
      "palworld fast travel locations, palworld statue of power map, palworld best base locations, palworld base site guide, palworld teleport map",
  },
  {
    slug: "sakurajima",
    name: "Sakurajima Island Map",
    short: "Expansion island, Bellanoir site and Pal Terminal.",
    desc: "The Sakurajima expansion map: sakura groves, the Bellanoir summoning shrine, the Pal Terminal and the level 45–55 spawn bands across the island.",
    layer: "island",
    palette: ["#F472B6", "#BE185D"],
    focus: ["sakurajima"],
    bosses: ["Bellanoir", "Bellanoir Libero"],
    strategy:
      "Arrive at level 45 minimum with Grade-3 armour and a Pal Metal weapon. Bellanoir is summoned from a Slab at an altar rather than found in the world, so farm slabs from the island's chests before you build the altar.",
    keywords:
      "palworld sakurajima map, sakurajima locations palworld, bellanoir location palworld, palworld sakurajima pals, palworld sakurajima level",
  },
  {
    slug: "feybreak",
    name: "Feybreak Island Map",
    short: "End-game ash island, Oculus Gate and level 60 alphas.",
    desc: "The Feybreak map: Scorched Ashland, Emberstone Plateau, Loess Plains, the Oculus Gate and the densest alpha cluster in Palworld.",
    layer: "island",
    palette: ["#EF4444", "#7F1D1D"],
    focus: ["feybreak"],
    bosses: ["Xenolord", "Alpha Nitemary", "Alpha Splatterina"],
    strategy:
      "Feybreak is the only region where hexolite quartz spawns, so bring a Mining Pal on every trip. Fight from a flier where possible: ground packs chain-aggro across the ash plains and a level 60 alpha train ends most level 55 runs.",
    keywords:
      "palworld feybreak map, feybreak locations palworld, xenolord location, palworld feybreak alphas, hexolite quartz map palworld",
  },
  {
    slug: "world-tree",
    name: "World Tree Raid Map",
    short: "The separate raid map and its wave layout.",
    desc: "The World Tree is its own instanced map. This view covers the arena layout, wave order and where the site's rarest Lucky variants are recorded.",
    layer: "raid",
    palette: ["#F59E0B", "#92400E"],
    focus: ["world-tree"],
    bosses: ["World Tree raid waves"],
    strategy:
      "Treat the World Tree as a damage race, not an exploration map. Bring four condensed Pals with mixed elements, stack Mega Health Potions, and clear waves at the arena edge so you never get surrounded.",
    keywords:
      "palworld world tree map, world tree palworld raid, palworld world tree location, world tree pals palworld, palworld raid map",
  },
  {
    slug: "sunreach-sky-islands",
    name: "Sunreach Sky Islands Map",
    short: "Floating isles, glide routes and skill-fruit trees.",
    desc: "The Sunreach sky islands added in Palworld 1.0: how to reach them, the glide chain between isles, and the skill-fruit and chest routes worth the trip.",
    layer: "island",
    palette: ["#C4B5FD", "#6D28D9"],
    focus: ["sunreach-sky-islands"],
    bosses: ["Sky-isle alpha spawns"],
    strategy:
      "You need a flier with high stamina — Faleris or better — and a spare Pal Sphere loadout, because a fall from the isles usually means a full run back. Plant a statue on the first isle you land on before looting anything.",
    keywords:
      "palworld sunreach map, sunreach sky islands palworld, palworld sky island locations, how to reach sunreach palworld, palworld 1.0 new map areas",
  },
];

export const MAP_VIEW_BY_SLUG: Record<string, MapView> = Object.fromEntries(
  MAP_VIEWS.map((m) => [m.slug, m]),
);

/** Guide slug for a map view — kept in one place so links never drift. */
export const mapGuideSlug = (view: MapView | string) =>
  `palworld-${typeof view === "string" ? view : view.slug}-map-guide`;

/* ------------------------------------------------------------------ *
 * Boss hotspots — every Tower / Raid / Predator boss pinned onto the
 * map canvas near the region named in its location string, with a link
 * straight to that boss's guide.
 * ------------------------------------------------------------------ */

export interface MapHotspot {
  bossSlug: string;
  name: string;
  pal: string;
  kind: string;
  element: string;
  level: number;
  color: string;
  regionSlug: string;
  regionName: string;
  guideSlug: string;
  x: number;
  y: number;
}

function regionForLocation(location: string): MapRegion | undefined {
  const loc = location.toLowerCase();
  return MAP_REGIONS.find(
    (r) => loc.includes(r.name.toLowerCase()) || r.spawnKeys.some((k) => loc.includes(k.toLowerCase())),
  );
}

export const MAP_HOTSPOTS: MapHotspot[] = (() => {
  const perRegion = new Map<string, number>();
  const out: MapHotspot[] = [];
  for (const b of BOSSES) {
    // Bosses whose arena is not tied to a mapped region (summoned raids, etc.)
    // get no world pin — they stay listed on the boss database instead.
    const region = regionForLocation(b.location);
    if (!region) continue;
    const i = perRegion.get(region.slug) ?? 0;
    perRegion.set(region.slug, i + 1);
    // Fan the pins around the region centre so overlapping bosses stay readable.
    const angle = (-Math.PI / 2) + i * (Math.PI * 2) / 5;
    const dist = i === 0 ? 0 : region.r + 16;
    out.push({
      bossSlug: b.slug,
      name: b.name,
      pal: b.pal,
      kind: b.kind,
      element: b.element,
      level: b.level,
      color: b.color,
      regionSlug: region.slug,
      regionName: region.name,
      guideSlug: `palworld-${b.slug}-boss-guide`,
      x: Math.round(region.cx + Math.cos(angle) * dist),
      y: Math.round(region.cy + Math.sin(angle) * dist),
    });
  }
  return out;
})();

export const hotspotsForRegion = (slug: string) => MAP_HOTSPOTS.filter((h) => h.regionSlug === slug);
